/**
 * Unified credential registry — the SINGLE source of truth for every API-key
 * integration OneStone has: AI model providers, AgentMail, and infrastructure
 * services (Cloudflare, Porkbun, GitHub).
 *
 * WHY (design SSOT proj_20260923003139_45d642): historically these were split
 * across two hardcoded registries with different storage, decrypt paths, API
 * surfaces, and UI panels — `PROVIDER_REGISTRY` (AI + email) vs. the infra
 * shapes + `PROVIDER_ENV_VAR`/`INFRA_SERVICES` in the broker. That asymmetry
 * becomes a mess the moment sync lands (provider keys have a cloud home; the
 * infra creds have none). This registry unifies them: one uniform descriptor
 * per service — `{ id, kind, group, fields[], envVars, … }` — that drives
 * storage, encryption, the credential broker's env mapping, and (later slices)
 * the cloud store, the Connections UI, and the sync policy.
 *
 * SLICE 1 (this file): the registry + types. `PROVIDER_REGISTRY` is now DERIVED
 * from it (see provider.ts) so there is exactly one place provider metadata
 * lives. Storage/broker/UI rewiring rides later slices — the infra entries are
 * present here but not yet consumed by the broker, which keeps this slice a pure
 * foundation with zero behavior change.
 *
 * `kind`: 'model' = an AI model provider (routed by the provider factory via
 * `transport`); 'tool' = a non-model integration (email service, infra CLI).
 * `group`: the Connections UI section — 'ai' | 'email' | 'infra'.
 *
 * This module is the BASE of the credential type graph — it imports nothing from
 * provider.ts (which instead derives ProviderId + PROVIDER_REGISTRY from here),
 * so the dependency edge is one-directional (provider → credential, no cycle).
 */

/**
 * Every credential-bearing integration, keyed by service id. The AI/email subset
 * (all but the three infra ids) is re-exposed as `ProviderId` in provider.ts.
 */
export type CredentialId =
  | 'anthropic'
  | 'openai'
  | 'ollama'
  | 'groq'
  | 'openrouter'
  | 'mistral'
  | 'gemini'
  | 'agentmail'
  | 'cloudflare'
  | 'porkbun'
  | 'github';

/** Whether a service is an AI model provider or a non-model tool integration. */
export type CredentialKind = 'model' | 'tool';

/** Connections-page section a credential renders under. */
export type CredentialGroup = 'ai' | 'email' | 'infra';

/** Model-provider wire transport (only meaningful for kind:'model'; 'none' otherwise). */
export type CredentialTransport = 'anthropic-sdk' | 'openai-compat' | 'ollama' | 'none';

/**
 * One field of a credential. A service may carry several (e.g. AgentMail's
 * apiKey + inbox, Cloudflare's apiToken + accountId). `secret: true` fields are
 * encrypted at rest and never returned in plaintext by any route; non-secret
 * fields (inbox, accountId, Ollama baseUrl) are public identifiers stored as-is.
 */
export interface CredentialField {
  /** Property name within the stored credential object (e.g. 'apiKey', 'inbox', 'apiToken', 'accountId'). */
  key: string;
  /** Human label for the field in the Connections UI. */
  label: string;
  /** Secret material (encrypted at rest, masked in responses) vs. a public identifier. */
  secret: boolean;
  /** Placeholder shown in the input. */
  placeholder?: string;
  /** Required value prefix for validation (e.g. 'sk-ant-'). Omit when the service has no stable prefix. */
  validatePrefix?: string;
  /**
   * A non-secret field the credential works without (Cloudflare's account ID,
   * Ollama's base URL — which falls back to the service's `baseUrl`). Every
   * other field must hold a value for the credential to save. Secrets are never
   * optional.
   */
  optional?: boolean;
  /**
   * Conventional environment-variable name(s) this field's decrypted value
   * populates for the credential broker (bash `useCredentials`). A single value
   * may fan out to several (GitHub's token → GH_TOKEN + GITHUB_TOKEN). Omit for
   * fields with no CLI-env convention (AI keys consumed only via the provider
   * factory, non-secret identifiers).
   */
  envVars?: string[];
}

/** A uniform descriptor for one credential-bearing integration. */
export interface CredentialSpec {
  id: CredentialId;
  /** Display name (e.g. 'Anthropic', 'Cloudflare'). */
  label: string;
  /** One-line description for the Connections card. */
  description: string;
  kind: CredentialKind;
  group: CredentialGroup;
  /** Logo asset path (served from the renderer's /providers/ dir). */
  logo: string;
  /** API base URL — model routing for kind:'model', documentation reference otherwise. */
  baseUrl: string;
  /** Where the user obtains the credential ("Get a key" link). Omit when none applies. */
  credentialUrl?: string;
  /** Ordered credential fields (secret + non-secret). Empty only for no-credential services. */
  fields: CredentialField[];
  /** Provider factory routing — only meaningful for kind:'model'; 'none' for tools. */
  transport: CredentialTransport;
  /**
   * OAuth linkability. Present when the credential can be sourced from an OAuth
   * login rather than a pasted key (GitHub-via-login on cloud — see design SSOT
   * §GitHub-via-OAuth). Scope details are deferred to the GitHub slice pending
   * verification of the live OAuth app; this marker only records linkability.
   */
  oauth?: { provider: string };
}

/**
 * THE registry. Order is significant for AI/email entries: it defines the
 * display order everywhere `PROVIDER_REGISTRY` is derived and iterated
 * (onboarding grid, Connections, provider-status loop, channel labels).
 */
export const CREDENTIAL_REGISTRY: CredentialSpec[] = [
  // --- AI model providers (group: 'ai') -----------------------------------
  {
    id: 'anthropic',
    label: 'Anthropic',
    description: 'Claude models — Opus, Sonnet, Haiku. Pay-per-token with prompt caching.',
    kind: 'model',
    group: 'ai',
    logo: './providers/anthropic.png',
    baseUrl: 'https://api.anthropic.com',
    credentialUrl: 'https://console.anthropic.com/settings/keys',
    transport: 'anthropic-sdk',
    fields: [{ key: 'apiKey', label: 'API Key', secret: true, placeholder: 'sk-ant-...', validatePrefix: 'sk-ant-' }],
  },
  {
    id: 'openai',
    label: 'OpenAI',
    description: 'GPT-5 and GPT-4.1 — flagship agentic reasoning plus fast, cheap variants. Pay-per-token.',
    kind: 'model',
    group: 'ai',
    logo: './providers/openai.png',
    // Host root — the OpenAI-compat provider appends `/v1/...`, so NO `/v1` here.
    baseUrl: 'https://api.openai.com',
    credentialUrl: 'https://platform.openai.com/api-keys',
    transport: 'openai-compat',
    // Matches both classic `sk-...` and project `sk-proj-...` keys.
    fields: [
      { key: 'apiKey', label: 'API Key', secret: true, placeholder: 'sk-... or sk-proj-...', validatePrefix: 'sk-' },
    ],
  },
  {
    id: 'openrouter',
    label: 'OpenRouter',
    description: 'Access 200+ models through one API key. Claude, GPT, Llama, Mistral, and more.',
    kind: 'model',
    group: 'ai',
    logo: './providers/openrouter.png',
    baseUrl: 'https://openrouter.ai/api',
    credentialUrl: 'https://openrouter.ai/settings/keys',
    transport: 'openai-compat',
    fields: [
      {
        key: 'apiKey',
        label: 'API Key',
        secret: true,
        placeholder: 'sk-or-...',
        validatePrefix: 'sk-or-',
        envVars: ['OPENROUTER_API_KEY'],
      },
    ],
  },
  {
    id: 'groq',
    label: 'Groq',
    description: 'Ultra-fast inference on Llama, Mixtral, Gemma. Free tier available.',
    kind: 'model',
    group: 'ai',
    logo: './providers/groq.png',
    baseUrl: 'https://api.groq.com/openai',
    credentialUrl: 'https://console.groq.com/keys',
    transport: 'openai-compat',
    fields: [
      {
        key: 'apiKey',
        label: 'API Key',
        secret: true,
        placeholder: 'gsk_...',
        validatePrefix: 'gsk_',
        envVars: ['GROQ_API_KEY'],
      },
    ],
  },
  {
    id: 'mistral',
    label: 'Mistral',
    description: 'Mistral Large, Medium, Small, and Codestral. European AI.',
    kind: 'model',
    group: 'ai',
    logo: './providers/mistral.png',
    baseUrl: 'https://api.mistral.ai',
    credentialUrl: 'https://console.mistral.ai/api-keys',
    transport: 'openai-compat',
    fields: [
      { key: 'apiKey', label: 'API Key', secret: true, placeholder: 'API key...', envVars: ['MISTRAL_API_KEY'] },
    ],
  },
  {
    id: 'gemini',
    label: 'Google Gemini',
    description: 'Gemini 2.5 Pro, Flash. Google AI Studio or Vertex AI.',
    kind: 'model',
    group: 'ai',
    logo: './providers/gemini.png',
    baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai',
    credentialUrl: 'https://aistudio.google.com/apikey',
    transport: 'openai-compat',
    fields: [{ key: 'apiKey', label: 'API Key', secret: true, placeholder: 'API key...', envVars: ['GEMINI_API_KEY'] }],
  },
  {
    id: 'ollama',
    label: 'Ollama',
    description: 'Free local models — Llama, Mistral, Phi, Qwen. No API key needed.',
    kind: 'model',
    group: 'ai',
    logo: './providers/ollama.png',
    baseUrl: 'http://localhost:11434',
    transport: 'ollama',
    // No secret — Ollama is a local base URL, not a key. Drives requiresApiKey=false.
    fields: [
      { key: 'baseUrl', label: 'Base URL', secret: false, placeholder: 'http://localhost:11434', optional: true },
    ],
  },

  // --- Email service (group: 'email') -------------------------------------
  {
    id: 'agentmail',
    label: 'AgentMail',
    description: 'Email for AI agents — send, receive, and manage email. Requires API key + inbox address.',
    kind: 'tool',
    group: 'email',
    logo: './providers/agentmail.png',
    baseUrl: 'https://api.agentmail.to/v0',
    transport: 'none',
    fields: [
      { key: 'apiKey', label: 'API Key', secret: true, placeholder: 'API key...' },
      { key: 'inbox', label: 'Inbox address', secret: false, placeholder: 'you@agentmail.to' },
    ],
  },

  // --- Infrastructure services (group: 'infra') ---------------------------
  {
    id: 'cloudflare',
    label: 'Cloudflare',
    description: 'Cloudflare API Token — manage Workers, Pages, DNS, and zones.',
    kind: 'tool',
    group: 'infra',
    logo: './providers/cloudflare.png',
    baseUrl: 'https://api.cloudflare.com',
    credentialUrl: 'https://dash.cloudflare.com/profile/api-tokens',
    transport: 'none',
    fields: [
      {
        key: 'apiToken',
        label: 'API Token',
        secret: true,
        placeholder: 'API token...',
        envVars: ['CLOUDFLARE_API_TOKEN'],
      },
      // Public identifier, stored plaintext — routes wrangler to the right account.
      {
        key: 'accountId',
        label: 'Account ID',
        secret: false,
        placeholder: 'Account ID (optional)',
        envVars: ['CLOUDFLARE_ACCOUNT_ID'],
        optional: true,
      },
    ],
  },
  {
    id: 'porkbun',
    label: 'Porkbun',
    description: 'Porkbun domain registrar — DNS management, domain registration, and transfers.',
    kind: 'tool',
    group: 'infra',
    logo: './providers/porkbun.png',
    baseUrl: 'https://api.porkbun.com',
    credentialUrl: 'https://porkbun.com/account/api',
    transport: 'none',
    fields: [
      { key: 'apikey', label: 'API Key', secret: true, placeholder: 'pk1_...', envVars: ['PORKBUN_API_KEY'] },
      {
        key: 'secretapikey',
        label: 'Secret API Key',
        secret: true,
        placeholder: 'sk1_...',
        envVars: ['PORKBUN_SECRET_API_KEY'],
      },
    ],
  },
  {
    id: 'github',
    label: 'GitHub',
    description: 'GitHub — repository and workflow access for the agent’s git operations.',
    kind: 'tool',
    group: 'infra',
    logo: './providers/github.png',
    baseUrl: 'https://api.github.com',
    credentialUrl: 'https://github.com/settings/tokens',
    transport: 'none',
    oauth: { provider: 'github' },
    // Single token fans out to both conventional env vars (gh CLI reads GH_TOKEN;
    // git/Actions read GITHUB_TOKEN).
    fields: [
      {
        key: 'token',
        label: 'Personal Access Token',
        secret: true,
        placeholder: 'ghp_... or github_pat_...',
        envVars: ['GH_TOKEN', 'GITHUB_TOKEN'],
      },
    ],
  },
];

/** Look up a credential spec by id. */
export function getCredentialSpec(id: string): CredentialSpec | undefined {
  return CREDENTIAL_REGISTRY.find((c) => c.id === id);
}

/** All credential ids as a readonly list. */
export const CREDENTIAL_IDS: readonly CredentialId[] = CREDENTIAL_REGISTRY.map((c) => c.id);
